import { useTranslation } from 'react-i18next';

import { Body1, Stack, Subtitle2 } from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import type { UnclaimedPrescriberOrderInfoFragment } from '../../../graphql/generated/graphql';
import { formatToLocalizedDate } from '../../../util';
import { getProductDescription } from '../../../util/formatting/prescriber-order/get-product-description';

type UnclaimedPrescriptionDescriptionProps = {
    prescriberOrder: UnclaimedPrescriberOrderInfoFragment;
};

export const UnclaimedPrescriptionDescription = (
    props: UnclaimedPrescriptionDescriptionProps
) => {
    const { prescriberOrder } = props;
    const { t, i18n } = useTranslation();
    const configOverride = useMedicationConfigOverride();

    const med = getFragment(prescriberOrder.prescribedMedicationUnclaimed);

    const ndcKey = med.id;
    let originalNdc: string | null | undefined;
    if (
        prescriberOrder.prescribedMedicationUnclaimed.__typename ===
        'UnknownMedication'
    ) {
        originalNdc = prescriberOrder.prescribedMedicationUnclaimed.originalNdc;
    }

    const medicationOverride = originalNdc
        ? configOverride.ndcInfo[originalNdc]
        : configOverride.ndcInfo[ndcKey];
    const description = getProductDescription(
        prescriberOrder,
        i18n,
        medicationOverride
    );

    const writtenDate = prescriberOrder.writtenDateUnclaimed
        ? formatToLocalizedDate(
              new Date(prescriberOrder.writtenDateUnclaimed),
              { dateStyle: 'long', timeZone: 'UTC' },
              i18n.language
          )
        : null;

    return (
        <Stack gap="xxs">
            <Subtitle2>{description}</Subtitle2>
            <Body1>
                {prescriberOrder.patientInitials
                    ? t('unclaimedPrescriberOrder.prescriberPatientInitials', {
                          prescriber: prescriberOrder.prescriberInitials,
                          patient: prescriberOrder.patientInitials,
                      })
                    : t('unclaimedPrescriberOrder.prescriberInitials', {
                          prescriber: prescriberOrder.prescriberInitials,
                      })}
            </Body1>
            {writtenDate && (
                <Body1>
                    {t('unclaimedPrescriberOrder.recievedOnDate', {
                        date: writtenDate,
                    })}
                </Body1>
            )}
        </Stack>
    );
};
